import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  useCollectionBinding,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
// Local pages.json — overrides layer for this fork (currently just Future
// Residents Club). Base oasis pages are fetched at component-mount time via
// `useBasePages()` below.
import localPagesData from '../pages.json';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Solid-icon glyphs that pair with the utility-row quick links.
// `faUsers` reads as "group of residents" for the Residents link;
// `faHandshake` reads as "agreement / sign on" for Apply Now. The
// canonical Weidner via6 reference renders these utility links as plain
// text (no glyph), but the oasis base ships them with icons and the
// surrounding visual treatment depends on the icon-before-label pairing,
// so the icons stay. The brand glyph imports (Facebook / X / Google)
// were dropped together with the social sub-cluster on the left side of
// the utility row — see the comment on the restructured left cluster
// below for the rationale.
import { faUsers, faHandshake } from '@fortawesome/free-solid-svg-icons';

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

/** Page aliases that should never appear in the main navigation. Note
 *  `home` is intentionally NOT here — the canonical Weidner via6 reference
 *  surfaces Home as the first nav item even though the base oasis hides
 *  it. `schedule-a-tour` and `virtual-tour` are also intentionally left
 *  in the nav (the base oasis excludes them) because via6 surfaces them
 *  as children of the synthetic "Gallery & Tours" umbrella dropdown. */
const NAV_EXCLUDED_ALIASES = new Set([
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'hyperlocal',
  'reviews',
]);

/**
 * Synthetic umbrella parents injected at nav-build time. These aren't real
 * pages — they're grouping headers that exist purely to give the user a
 * cleaner top-level nav by collapsing related pages into hover dropdowns.
 * Matches the via6 wrapper's `injects synthetic features / gallery-tours
 * umbrella parents` behavior.
 *
 * `path` points at the first child so clicking the parent label (or
 * tapping it on mobile, where dropdown hover doesn't apply) lands the
 * visitor on a real page rather than a dead URL. Position values are
 * chosen to slot between Floor Plans / Find Your Home (position 1 in
 * base oasis) and Neighborhood (position 4) so the umbrella headers land
 * where via6 places them — Floor Plans (or Find Your Home ▼) → Features
 * → Gallery & Tours → Neighborhood.
 */
interface UmbrellaParent {
  alias: string;
  name: string;
  path: string;
  position: number;
  children: string[];
}
const UMBRELLA_PARENTS: UmbrellaParent[] = [
  {
    alias: 'features',
    name: 'Features',
    path: '/amenities',
    position: 2,
    children: ['amenities', 'pet-policy', 'future-residents-club'],
  },
  {
    alias: 'gallery-tours',
    name: 'Gallery & Tours',
    path: '/gallery',
    position: 3,
    children: ['gallery', 'schedule-a-tour', 'virtual-tour'],
  },
];

interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * Apply the via6 umbrella-parent structure on top of a raw pages map.
 * Injects synthetic `features` / `gallery-tours` entries and rewrites
 * the `parent` of each umbrella child so the existing parent/child
 * resolution in `buildNavLinks` produces a [Home, Floor Plans (or Find
 * Your Home ▼ when Interactive Map is present), Features (dropdown),
 * Gallery & Tours (dropdown), Neighborhood, Contact] top level that
 * mirrors site-via-six.pages.dev. Operates on a shallow copy so the
 * source pagesData (an imported JSON module) isn't mutated.
 *
 * Each child's `position` is also overwritten to follow its index in the
 * umbrella's `children` array (offset by 0.01 from the parent so children
 * stay tightly grouped under their parent in the sort). Without this the
 * children would sort by whatever position they have in the underlying
 * pages.json — e.g. Gallery (position 5), Virtual Tour (position 6),
 * Schedule a Tour (position 9) — which is not the order the via6
 * reference renders. The author-controlled `children: ['gallery',
 * 'schedule-a-tour', 'virtual-tour']` array is the canonical order.
 */
function applyUmbrellaStructure(
  raw: Record<string, PageEntry>,
): Record<string, PageEntry> {
  const copy: Record<string, PageEntry> = {};
  for (const [alias, page] of Object.entries(raw)) {
    copy[alias] = { ...page };
  }
  for (const umbrella of UMBRELLA_PARENTS) {
    copy[umbrella.alias] = {
      name: umbrella.name,
      path: umbrella.path,
      position: umbrella.position,
      visible: true,
    };
    umbrella.children.forEach((childAlias, childIndex) => {
      if (copy[childAlias]) {
        copy[childAlias] = {
          ...copy[childAlias],
          parent: umbrella.alias,
          // Bias position so children sort in author-declared order
          // (e.g. gallery → schedule-a-tour → virtual-tour) regardless
          // of their underlying pages.json positions.
          position: umbrella.position + 0.01 * (childIndex + 1),
        };
      }
    });
  }
  return copy;
}

/**
 * Build navigation links from the merged pages source. Only includes pages
 * that are visible and not in the exclusion list. Sorted by position.
 * Pages declaring `parent: <alias>` are nested as `children` of their
 * parent entry rather than appearing at the top level. Orphaned children
 * (parent missing or hidden) fall back to the top level so a page is
 * never silently dropped from the nav.
 *
 * When the property has an Interactive Map (Engrain SightMap URL), the
 * Floor Plans top-level item is promoted to a "Find Your Home" dropdown
 * whose children are Floor Plans + Interactive Map (in that order).
 * Without an Interactive Map, Floor Plans stays a plain top-level link.
 */
function buildNavLinks(
  pagesData: Record<string, PageEntry>,
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
): NavLink[] {
  // When Interactive Map is available, inject a synthetic "Find Your
  // Home" umbrella and nest both Floor Plans and Interactive Map under
  // it — same pattern as Features / Gallery & Tours. Without the map,
  // Floor Plans stays a plain top-level link and Interactive Map is
  // filtered out below.
  let source = applyUmbrellaStructure(pagesData);
  if (
    hasInteractiveLeasing &&
    source['floor-plans'] &&
    source['interactive-map'] &&
    source['interactive-map'].visible !== false
  ) {
    const floorPlans = source['floor-plans'];
    const floorPlansPosition = floorPlans.position ?? 1;
    source = { ...source };
    source['find-your-home'] = {
      name: 'Find Your Home',
      path: floorPlans.path || '/floor-plans',
      position: floorPlansPosition,
      visible: true,
    };
    source['floor-plans'] = {
      ...floorPlans,
      parent: 'find-your-home',
      position: floorPlansPosition + 0.01,
    };
    source['interactive-map'] = {
      ...source['interactive-map'],
      parent: 'find-your-home',
      position: floorPlansPosition + 0.02,
    };
  }

  const eligible = Object.entries(source)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path });
  }

  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  return topLevel;
}

/**
 * Lazy-load the base oasis pages.json at component-mount time so the
 * merged nav reflects the full inheritance chain. A static
 * `import basePagesData from '@vibesites/theme-oasis/pages.json'` would
 * trip the schema linter (which uses raw Node ESM resolution and can't
 * see the Vite alias map at `repos/.generated/vite-aliases.json`), and a
 * top-level `await import(...)` would make this module async — which
 * cascades up through `components/index.ts` → `registry.ts` and forces
 * every consumer to support async theme modules. A useEffect-driven
 * dynamic import sidesteps both: the linter loads the module without
 * triggering the effect (so the import never executes at lint time), and
 * Vite's prefix alias resolves `@vibesites/theme-oasis/pages.json` →
 * `repos/vibesites-theme-oasis/src/pages.json` at preview/build time.
 *
 * Returns the merged map (base + local override). Merge is per-alias
 * shallow — matching the deploy pipeline's pages.json inheritance — so a
 * local entry that only overrides `sections` (e.g. `floor-plans`) keeps
 * the base `name` / `path` / `position` / `visible` that the nav needs.
 * A top-level `{ ...base, ...local }` spread would replace the whole page
 * object and drop Floor Plans (and any similar sections-only override)
 * from the header. Until the dynamic import resolves the merge falls
 * back to the local layer alone, which means the very first paint of the
 * nav on a cold preview may show only the locally-declared pages. The
 * effect resolves on the next tick and the nav reconciles.
 */
function useMergedPages(): Record<string, PageEntry> {
  const [basePages, setBasePages] = useState<Record<string, PageEntry>>({});
  useEffect(() => {
    let cancelled = false;
    import('@vibesites/theme-oasis/pages.json')
      .then((mod) => {
        if (cancelled) return;
        const data = (mod.default ?? mod) as Record<string, PageEntry>;
        setBasePages(data);
      })
      .catch(() => {
        // Resolution failed (e.g. lint-time Node ESM with no Vite alias
        // for the deep specifier). Leave basePages empty — the nav will
        // render with only the local override layer.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => {
    const local = localPagesData as unknown as Record<string, PageEntry>;
    const merged: Record<string, PageEntry> = { ...basePages };
    for (const [alias, override] of Object.entries(local)) {
      merged[alias] = { ...merged[alias], ...override };
    }
    return merged;
  }, [basePages]);
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_black_logo', suggestedFields: ['property_mono_black_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// NOTE: The slot group path stays `header.resident_access_link` (and the URL
// stays `/resident-access`) so previously-saved content keeps mapping to the
// same field. The visible label was shortened from "Resident Access" to
// "Residents" to match the reference demo's terser utility-row label.
const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Residents Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Residents' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Address row — promoted to a slot group on the base oasis so the
// editor can pick address text, destination URL, and link target
// independently. Now also rendered inline on the desktop utility row's
// left cluster (next to the phone link) to mirror the via6 layout, in
// addition to the mobile drawer footer.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_place_url',
      suggestedFields: ['property_google_place_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// View Floor Plans CTA inside the mobile drawer.
const viewFloorPlansLinkDef: SlotGroupDefinition = {
  label: 'View Floor Plans Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ============================================================================
// Chevron Down Icon for "More" dropdown
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

// ============================================================================
// Desktop Nav Link
// ============================================================================

function DesktopNavLink({ link }: { link: NavLink }) {
  const hasChildren = !!(link.children && link.children.length);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        className="oasis-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 whitespace-nowrap"
      >
        {link.label}
      </a>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <a
        href={link.href}
        className="oasis-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
        aria-haspopup="true"
        aria-expanded={open}
        onFocus={() => setOpen(true)}
      >
        {link.label}
        <ChevronDownIcon
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </a>

      <div
        className={`nav-more-dropdown absolute left-0 top-full mt-1 border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1'
        }`}
        style={{ backgroundColor: 'var(--vibe-color-background)' }}
      >
        {link.children!.map((child) => (
          <a
            key={child.href}
            href={child.href}
            className="oasis-nav-darken-hover block px-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors"
          >
            {child.label}
          </a>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Desktop Navigation with dynamic "More" overflow dropdown
// ============================================================================

function DesktopNav({ navLinks }: { navLinks: NavLink[] }) {
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    const computedGap = parseFloat(getComputedStyle(container).gap) || 20;
    const moreButtonWidth = 90;

    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const widthWithGap = i === 0 ? childWidth : childWidth + computedGap;
      const needed = totalWidth + widthWithGap + (i < children.length - 1 ? moreButtonWidth + computedGap : 0);

      if (needed > availableWidth && i < children.length) {
        const remainingWidth = totalWidth + widthWithGap;
        let allFit = true;
        let testWidth = remainingWidth;
        for (let j = i + 1; j < children.length; j++) {
          testWidth += children[j].offsetWidth + computedGap;
          if (testWidth > availableWidth) {
            allFit = false;
            break;
          }
        }
        if (allFit) {
          fitCount = children.length;
        } else {
          fitCount = i;
        }
        break;
      }
      totalWidth += widthWithGap;
      fitCount = i + 1;
    }

    setVisibleCount(fitCount);
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();
    const observer = new ResizeObserver(() => recalculate());
    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  return (
    <>
      <div
        ref={measureRef}
        aria-hidden="true"
        className="flex items-center gap-5 absolute pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}
      >
        {navLinks.map((link) => (
          <span
            key={link.href}
            className="font-body text-[13px] font-semibold tracking-wider uppercase inline-flex items-center gap-1"
          >
            {link.label}
            {link.children && link.children.length ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : null}
          </span>
        ))}
      </div>

      <nav
        ref={navContainerRef}
        className="hidden lg:flex items-center justify-center gap-5 xl:gap-7 flex-1 min-w-0 mx-6"
        role="navigation"
        aria-label="Main navigation"
      >
        {visibleLinks.map((link) => (
          <DesktopNavLink key={link.href} link={link} />
        ))}

        {overflowLinks.length > 0 && (
          <div
            ref={moreRef}
            className="relative"
            onMouseEnter={() => setMoreOpen(true)}
            onMouseLeave={() => setMoreOpen(false)}
          >
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              onFocus={() => setMoreOpen(true)}
              className="oasis-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              More
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
            </button>

            <div
              className={`nav-more-dropdown absolute right-0 top-full mt-1 border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
                moreOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-1'
              }`}
              style={{ backgroundColor: 'var(--vibe-color-background)' }}
            >
              {overflowLinks.map((link) => (
                <React.Fragment key={link.href}>
                  <a
                    href={link.href}
                    className="oasis-nav-darken-hover block px-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors"
                  >
                    {link.label}
                  </a>
                  {link.children?.map((child) => (
                    <a
                      key={child.href}
                      href={child.href}
                      className="oasis-nav-darken-hover block pl-9 pr-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text-muted)] transition-colors"
                    >
                      {child.label}
                    </a>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

// ============================================================================
// Mobile Nav Link
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
  isChild,
}: {
  link: NavLink;
  onNavigate: () => void;
  /** Drop-down (sub-nav) pages render distinctly from their parent: indented
      and in a muted weight/color, but still uppercase to match the parent
      rows and the desktop nav. This preserves the parent→child hierarchy the
      desktop hover dropdown conveys, inside the drawer's vertical list. */
  isChild?: boolean;
}) {
  return (
    <a
      href={link.href}
      onClick={onNavigate}
      className={
        isChild
          ? 'oasis-nav-darken-hover flex items-center gap-2.5 py-3 pl-12 pr-6 font-body text-sm font-medium uppercase tracking-wide text-[var(--vibe-color-text-muted)] transition-colors border-b border-[var(--vibe-color-border)]/50'
          : 'oasis-nav-darken-hover block py-3.5 px-6 font-body text-sm font-semibold uppercase tracking-wider text-[var(--vibe-color-text)] transition-colors border-b border-[var(--vibe-color-border)]/50'
      }
    >
      {link.label}
    </a>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// Weidner Oasis fork of the base oasis Header. Structural changes vs the
// base, all targeted at matching the canonical Weidner via6 reference
// (Repli-Multihub/vibesites-weidner-apartment-homes-via6):
//
//   1. Desktop utility row LEFT cluster: replaced the Facebook / X / Google
//      social-icon sub-cluster with an address link (MapPinIcon + street
//      address from the existing `header.address_link` slot). The via6
//      utility row has no social channels at all — phone + address are
//      the entire left side — and the social channels live in the footer
//      instead. We follow suit.
//   2. Desktop utility row RIGHT cluster: order is [Residents | Apply Now],
//      matching the via6 reference's post-DOM-swap visible order. Oasis
//      already ships Residents-before-Apply so no runtime swap is needed.
//   3. Mobile drawer: [Residents | Apply Now] grid mirrors the desktop
//      utility row order.
//   4. Nav source: per-alias shallow-merged base oasis + local pages.json
//      via the `useMergedPages` hook, with `applyUmbrellaStructure`
//      injecting synthetic Features and Gallery & Tours dropdown parents
//      so the top-level reads [Home, Floor Plans (or Find Your Home ▼
//      when Interactive Map is present), Features ▼, Gallery & Tours ▼,
//      Neighborhood, Contact Us].
//
// The contentSchema below drops the social-link slot groups (they no
// longer render). All other slot paths and tokens stay identical to the
// base so previously-saved content keeps mapping cleanly.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map gate — driven by the presence of the Engrain SightMap
  // URL custom field. When the SightMap URL is empty/absent the Interactive
  // Map page is dropped from the nav entirely. The field ships under two
  // accepted spellings (the engrain-sitemap block reads both):
  // `custom_engrain_sightmap_url` (primary) and `custom_engrain_sitemap_url`
  // (fallback). This mirrors the build-time gate in ssg-entry.ts, which
  // sets the page `visible: false` + `noindex: true` under the same
  // condition. (Previously this gated on the ApartmentSync JS + config IDs;
  // the map now renders from the Engrain SightMap URL, so that is the field
  // that decides whether the page should surface.)
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const engrainSightmapUrl = (propertyForLeasing?.custom_engrain_sightmap_url ??
    propertyForLeasing?.custom_engrain_sitemap_url) as string | null | undefined;
  const hasInteractiveLeasing = !!(engrainSightmapUrl && String(engrainSightmapUrl).trim());

  const pagesData = useMergedPages();
  const navLinks = useMemo(
    () => buildNavLinks(pagesData, hasVirtualTours, hasInteractiveLeasing),
    [pagesData, hasVirtualTours, hasInteractiveLeasing],
  );

  // Content tokens
  const phone = useContentToken('header.phone');

  // Slot groups
  // The logo defaults to the property's mono-black asset (see `logoSlotDef` /
  // the `header.logo` schema, which now inherits the base oasis mono-black
  // default rule). The header always sits on a white background, so the
  // mono-black default renders correctly without the old runtime swap that
  // re-pointed a mono-white selection to its mono-black equivalent.
  const logo = useSlotGroup('header.logo', logoSlotDef);

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  const viewFloorPlansLink = useSlotGroup('header.view_floor_plans_link', viewFloorPlansLinkDef);

  // Google Fonts — inject Playfair Display (heading) + Nunito Sans (body).
  useEffect(() => {
    document.querySelectorAll('link[data-vibe-font]').forEach((el) => el.remove());

    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    preconnect1.setAttribute('data-vibe-font', 'preconnect-gfonts');
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    preconnect2.setAttribute('data-vibe-font', 'preconnect-gstatic');
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&family=Nunito+Sans:wght@400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'theme-fonts');
    document.head.appendChild(link);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&family=Nunito+Sans:wght@400;500;600;700&display=swap"
      />

      <header
        ref={headerRef}
        className="fixed left-0 right-0 z-50 w-full"
        style={{
          top: 0,
          paddingTop: 'var(--offer-banner-height, 0px)',
          transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: 'var(--vibe-color-background)',
        }}
      >
        {/* ============================================================== */}
        {/* Utility row — sits above the main nav, desktop-only            */}
        {/* ============================================================== */}
        <div className="header-utility-row hidden lg:block">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-3 py-2.5">
              {/* Left cluster — phone | divider | address. Replaces the
                  base oasis social-icon sub-cluster (Facebook / X /
                  Google) so the row mirrors the via6 reference, where
                  the utility row is exclusively contact info and the
                  social channels live in the footer instead. */}
              <div className="flex items-center gap-3">
                {/* Phone */}
                <a
                  href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                  className="dniphone dniphonehref utility-row-link"
                  aria-label="Call us"
                >
                  <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  <EditableToken
                    path="header.phone"
                    defaultField="property_call_tracking_phone"
                    defaultValue="(555) 555-5555"
                    as="span"
                  >
                    {formatPhone(phone)}
                  </EditableToken>
                </a>

                <span className="utility-row-divider" aria-hidden="true" />

                {/* Address — opens the Google Maps place page in a new tab
                    by default. Reuses the existing `header.address_link`
                    slot group so the URL + label can be customized from
                    the editor without introducing a new path. */}
                <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                  <a
                    href={addressLink.url || '#'}
                    target={addressLink._target || '_blank'}
                    rel="noopener noreferrer"
                    className="utility-row-link"
                    aria-label="View on Google Maps"
                  >
                    <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{addressLink.address}</span>
                  </a>
                </EditableSlotGroup>
              </div>

              {/* Right cluster — Residents | divider | Apply Now. The via6
                  reference renders this cluster in HTML order [Apply Now |
                  Residents] and then DOM-swaps them after mount so the
                  visible order ends up [Residents | Apply Now]. Oasis
                  doesn't have that swap logic — we just author the
                  resolved order directly in the JSX. */}
              <div className="flex items-center gap-3">
                {/* Residents — pairs with a two-person "group" icon that
                    reads as "head of household". Placed before Apply Now
                    because the resident-portal action is higher-frequency
                    for the existing-tenant audience that lives in the
                    utility row. */}
                <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                  <a
                    href={residentAccessLink.url || '/resident-access'}
                    target={residentAccessLink._target || '_self'}
                    className="utility-row-link"
                  >
                    <FontAwesomeIcon icon={faUsers} className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                    {residentAccessLink.label || 'Residents'}
                  </a>
                </EditableSlotGroup>

                <span className="utility-row-divider" aria-hidden="true" />

                {/* Apply Now — handshake glyph reads as "agreement / sign
                    on", framing the lease as a relationship rather than
                    paperwork. */}
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '/apply-now'}
                    target={applyNowLink._target || '_self'}
                    className="utility-row-link"
                  >
                    <FontAwesomeIcon icon={faHandshake} className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Single unified navigation bar                                  */}
        {/* ============================================================== */}
        <div className="header-bar">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-20 lg:h-24">
              {/* Logo */}
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a
                  href="/"
                  className="flex-shrink-0 flex items-center h-16 lg:h-20 overflow-hidden"
                >
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logo.alt || 'Community Logo'}
                      className="max-h-full w-auto object-contain"
                    />
                  ) : (
                    <div className="font-heading text-xl lg:text-2xl font-bold text-[var(--vibe-color-primary)]">
                      The Oasis
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop Navigation Links */}
              <DesktopNav navLinks={navLinks} />

              {/* Right group — desktop Schedule-a-Tour CTA + mobile hamburger */}
              <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    className="header-nav-cta hidden lg:inline-flex"
                  >
                    {scheduleTourLink.label || 'Schedule a Tour'}
                  </a>
                </EditableSlotGroup>

                {/* Mobile Hamburger */}
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="lg:hidden relative w-10 h-10 flex items-center justify-center"
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={menuOpen}
                >
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? 'rotate-45' : '-translate-y-2'
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? 'opacity-0' : ''
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? '-rotate-45' : 'translate-y-2'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Mobile Menu Overlay                                            */}
        {/* ============================================================== */}
        <div
          className={`mobile-menu-panel lg:hidden fixed inset-x-0 bottom-0 overflow-y-auto transition-all duration-300 z-[61] ${
            menuOpen
              ? 'opacity-100 visible'
              : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{
            top: 'calc(var(--offer-banner-height, 0px) + 5rem)',
            backgroundColor: 'var(--vibe-color-background)',
          }}
        >
          {/* Navigation links */}
          <nav role="navigation" aria-label="Mobile navigation">
            {navLinks.map((link) => (
              <React.Fragment key={link.href}>
                <MobileNavLink link={link} onNavigate={closeMobileMenu} />
                {link.children?.map((child) => (
                  <MobileNavLink
                    key={child.href}
                    link={child}
                    onNavigate={closeMobileMenu}
                    isChild
                  />
                ))}
              </React.Fragment>
            ))}
          </nav>

          {/* Schedule a Tour CTA — primary conversion entry point for the
              mobile drawer. Mirrors the desktop nav-row CTA. */}
          <div className="px-5 pt-5 pb-2">
            <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
              <a
                href={scheduleTourLink.url || '/schedule-a-tour'}
                target={scheduleTourLink._target || '_self'}
                onClick={closeMobileMenu}
                className="mobile-util-pill block text-center w-full py-3 font-body text-sm font-medium tracking-[0.15em] uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
              >
                {scheduleTourLink.label || 'Schedule a Tour'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* View Floor Plans CTA */}
          <div className="px-5 pb-3">
            <EditableSlotGroup groupPath="header.view_floor_plans_link" definition={viewFloorPlansLinkDef}>
              <a
                href={viewFloorPlansLink.url || '/floor-plans'}
                target={viewFloorPlansLink._target || '_self'}
                onClick={closeMobileMenu}
                className="mobile-util-pill block text-center w-full py-3.5 font-body text-sm font-medium tracking-[0.15em] uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
              >
                {viewFloorPlansLink.label || 'View Floor Plans'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Utility CTA Buttons — Residents first to mirror the desktop
              utility-row order. */}
          <div className="px-5 pb-3">
            <div className="grid grid-cols-2 gap-2">
              <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                <a
                  href={residentAccessLink.url || '/resident-access'}
                  target={residentAccessLink._target || '_self'}
                  className="mobile-util-pill block text-center py-2.5 font-body text-[11px] font-medium tracking-wider uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
                >
                  {residentAccessLink.label || 'Residents'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_self'}
                  className="mobile-util-pill block text-center py-2.5 font-body text-[11px] font-medium tracking-wider uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Address & Phone footer.
              Extra bottom padding (pb-40) reserves clearance for the
              Rentgrata "Ask a Real Resident" floating widget, which is
              injected at runtime and pins itself to the bottom of the
              viewport. Without this the widget's fixed click surface
              overlaps the Residents / Apply Now buttons above and the
              address/phone footer, making them un-tappable on mobile.
              The drawer is `overflow-y-auto`, so this padding simply adds
              scrollable room letting every control clear the widget. */}
          <div className="px-5 pb-40 pt-3 border-t border-[var(--vibe-color-border)]/50">
            <div className="space-y-3">
              <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                <a
                  href={addressLink.url || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors"
                >
                  <MapPinIcon className="w-4 h-4 text-[var(--vibe-color-primary)] flex-shrink-0" />
                  <span className="oasis-nav-darken-hover">{addressLink.address}</span>
                </a>
              </EditableSlotGroup>
              <a
                href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref flex items-center gap-2.5 text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors"
              >
                <PhoneIcon className="w-4 h-4 text-[var(--vibe-color-primary)] flex-shrink-0" />
                <EditableToken
                  path="header.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                  as="span"
                  className="oasis-nav-darken-hover"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>
          </div>
        </div>
      </header>

      <style>{`
        /* ================================================================ */
        /* Global body typography — matches base oasis treatment            */
        /* ================================================================ */
        body {
          font-family: 'Nunito Sans', var(--vibe-font-body), system-ui, -apple-system, sans-serif !important;
          line-height: 1.92;
        }
        .font-body {
          font-family: 'Nunito Sans', var(--vibe-font-body), system-ui, -apple-system, sans-serif !important;
        }

        /* ================================================================ */
        /* Utility row (above the main nav)                                */
        /* ================================================================ */
        .header-utility-row {
          background-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }

        .utility-row-link {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.25rem 0.25rem;
          font-family: var(--vibe-font-body);
          font-size: 0.78rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          color: var(--vibe-color-text-on-primary);
          opacity: 0.85;
          text-decoration: none;
          white-space: nowrap;
          transition: opacity 0.2s ease;
        }
        .utility-row-link:hover {
          opacity: 1;
        }

        .utility-row-divider {
          display: block;
          width: 1px;
          height: 14px;
          background: color-mix(in srgb, var(--vibe-color-text-on-primary) 35%, transparent);
          flex-shrink: 0;
        }

        /* ================================================================ */
        /* Schedule-a-Tour CTA in the main nav bar (desktop only)          */
        /* ================================================================ */
        .header-nav-cta {
          align-items: center;
          gap: 0.5rem;
          padding: 0.875rem 2.5rem;
          background-color: var(--vibe-color-primary);
          border: none;
          border-radius: 2px;
          color: var(--vibe-color-text-on-primary);
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: 0.05em;
          text-decoration: none;
          white-space: nowrap;
          transition: background-color 0.25s ease, color 0.25s ease, transform 0.25s ease;
        }
        .header-nav-cta:hover {
          background-color: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
          transform: translateY(-1px);
        }
        .header-nav-cta:active {
          transform: translateY(0);
        }

        /* ================================================================ */
        /* Single navigation bar                                           */
        /* ================================================================ */
        .header-bar {
          border-bottom: 1px solid transparent;
        }

        .mobile-menu-panel a,
        .mobile-menu-panel button {
          color: var(--vibe-color-text);
        }

        .oasis-nav-darken-hover {
          transition: color 0.2s ease, font-weight 0.2s ease;
        }
        .oasis-nav-darken-hover:hover {
          color: var(--vibe-color-primary);
          font-weight: 700;
        }
        .mobile-menu-panel a:hover .oasis-nav-darken-hover,
        .mobile-menu-panel a:focus-visible .oasis-nav-darken-hover {
          color: var(--vibe-color-primary);
          font-weight: 700;
        }
        .mobile-menu-panel .mobile-util-pill:hover {
          color: var(--vibe-color-text-on-primary);
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================
// Mirrors the base oasis schema with one structural delta:
//   - Removed `header.facebook_link`, `header.twitter_link`, and
//     `header.google_link` (no longer rendered — utility row no longer
//     hosts a social sub-cluster).
// All other slot paths and tokens are unchanged so previously-saved
// content keeps mapping cleanly.
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_black_logo', suggestedFields: ['property_mono_black_logo', 'property_primary_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Residents Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Residents' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_google_place_url',
          suggestedFields: ['property_google_place_url'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.view_floor_plans_link': {
      label: 'View Floor Plans Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
